import { Injectable, OnDestroy, computed, inject, signal } from '@angular/core';
import {
  Observable,
  Subject,
  Subscription,
  catchError,
  debounceTime,
  distinctUntilChanged,
  finalize,
  map,
  of,
  switchMap,
  throwError,
} from 'rxjs';
import { CementoVentaApiService } from '../services/cemento-venta-api.service';
import { CementoVentaDto } from '../models/venta-cemento.dto';

const DEFAULT_PAGE = 1;
const DEFAULT_PAGE_SIZE = 20;
const DEFAULT_ORDERING = '-date_buy';

export interface CementoVentaVm extends CementoVentaDto {
  readonly trackId: string;
}

@Injectable()
export class VentasCementoStore implements OnDestroy {
  private readonly api = inject(CementoVentaApiService);
  private readonly loadingDepth = signal(0);
  private readonly searchChanges$ = new Subject<string>();
  private readonly searchSubscription: Subscription;

  readonly items = signal<CementoVentaVm[]>([]);
  readonly totalItems = signal(0);
  readonly page = signal(DEFAULT_PAGE);
  readonly pageSize = signal(DEFAULT_PAGE_SIZE);
  readonly searchTerm = signal('');
  readonly ordering = signal(DEFAULT_ORDERING);
  readonly error = signal<string | null>(null);
  readonly loading = computed(() => this.loadingDepth() > 0);
  readonly totalPages = computed(() => Math.max(1, Math.ceil(this.totalItems() / this.pageSize())));
  readonly hasPreviousPage = computed(() => this.page() > 1);
  readonly hasNextPage = computed(() => this.page() < this.totalPages());
  readonly pageStartIndex = computed(() => {
    if (this.totalItems() === 0) {
      return 0;
    }
    return (this.page() - 1) * this.pageSize() + 1;
  });
  readonly pageEndIndex = computed(() => Math.min(this.totalItems(), this.page() * this.pageSize()));

  constructor() {
    this.searchSubscription = this.searchChanges$
      .pipe(
        debounceTime(350),
        distinctUntilChanged(),
        switchMap(() => this.loadVentasInternal({ propagateError: false })),
      )
      .subscribe();
  }

  ngOnDestroy(): void {
    this.searchSubscription.unsubscribe();
  }

  loadVentas(): Observable<void> {
    return this.loadVentasInternal({ propagateError: true });
  }

  setSearchTerm(value: string): void {
    const normalized = String(value ?? '');
    if (this.searchTerm() === normalized) {
      return;
    }
    this.searchTerm.set(normalized);
    this.page.set(DEFAULT_PAGE);
    this.searchChanges$.next(normalized);
  }

  setOrdering(value: string): void {
    const normalized = value.trim() || DEFAULT_ORDERING;
    if (this.ordering() === normalized) {
      return;
    }
    this.ordering.set(normalized);
    this.page.set(DEFAULT_PAGE);
    this.loadVentasInternal({ propagateError: false }).subscribe();
  }

  setPage(value: number): void {
    const pageValue = Number.isFinite(value) ? Math.max(DEFAULT_PAGE, Math.trunc(value)) : DEFAULT_PAGE;
    if (this.page() === pageValue) {
      return;
    }
    this.page.set(pageValue);
    this.loadVentasInternal({ propagateError: false }).subscribe();
  }

  setPageSize(value: number): void {
    const sizeValue = Number.isFinite(value) ? Math.max(1, Math.trunc(value)) : DEFAULT_PAGE_SIZE;
    if (this.pageSize() === sizeValue) {
      return;
    }
    this.pageSize.set(sizeValue);
    this.page.set(DEFAULT_PAGE);
    this.loadVentasInternal({ propagateError: false }).subscribe();
  }

  clearError(): void {
    this.error.set(null);
  }

  exportExcel(): Observable<Blob> {
    return this.api.exportExcel({
      page: 1,
      pageSize: 1000,
      search: this.searchTerm(),
      ordering: this.ordering(),
    });
  }

  private loadVentasInternal(options: { propagateError: boolean }): Observable<void> {
    this.beginLoading();
    return this.api
      .listVentas({
        page: this.page(),
        pageSize: this.pageSize(),
        search: this.searchTerm().trim(),
        ordering: this.ordering(),
      })
      .pipe(
        map((response) => {
          this.totalItems.set(Math.max(0, Number(response.count) || 0));
          const effectiveLastPage = this.totalPages();
          if (this.page() > effectiveLastPage) {
            this.page.set(effectiveLastPage);
          }
          this.items.set(response.results.map(i => ({ ...i, trackId: String(i.id) })));
          this.error.set(null);
        }),
        catchError((error) => {
          this.items.set([]);
          this.totalItems.set(0);
          this.error.set('No se pudieron cargar las ventas.');
          if (options.propagateError) {
            return throwError(() => error);
          }
          return of(undefined);
        }),
        finalize(() => this.endLoading()),
      );
  }

  private beginLoading(): void {
    this.loadingDepth.update((depth) => depth + 1);
  }

  private endLoading(): void {
    this.loadingDepth.update((depth) => Math.max(0, depth - 1));
  }
}
